import { Button, Checkbox, ComboBox } from '@carbon/react';
import { Copy } from '@carbon/icons-react';
import {
  asProblem,
  FormModal,
  hasPermission,
  problemMessage,
  useAdmin,
  useReferenceOptions,
  type ReferenceOption,
} from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';

/**
 * The shift list's toolbar (ADR 0038): copy another store's shifts, and its shift settings, to a store. The copied shifts
 * replace the store's own; the store receives them with its next sync.
 */
export function CopyShiftsToolbar({ refresh }: { refresh(): void }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const stores = useReferenceOptions('org.store');
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string>();
  const [source, setSource] = useState<string>();
  const [withSettings, setWithSettings] = useState(true);

  const copy = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/shifts/stores/{storeId}/copy-from/{sourceStoreId}', {
        params: {
          path: { storeId: target ?? '', sourceStoreId: source ?? '' },
          query: { includeSettings: withSettings },
        },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: () => {
      setOpen(false);
      refresh();
    },
  });

  if (!hasPermission(session, 'ho.shifts.manage')) return null;
  const options = stores.data ?? [];
  const label = (option: ReferenceOption | null | undefined) => option?.label ?? '';

  return (
    <>
      <Button kind="ghost" renderIcon={Copy} onClick={() => setOpen(true)}>
        {t('shiftSetup.copy.button')}
      </Button>
      {copy.isSuccess && <span role="status">{t('shiftSetup.copy.done', { count: Number(copy.data.periods) })}</span>}
      {open && (
        <FormModal
          size="sm"
          title={t('shiftSetup.copy.title')}
          primaryText={t('shiftSetup.copy.confirm')}
          disabled={!target || !source || target === source}
          pending={copy.isPending}
          error={copy.error ? problemMessage(t, asProblem(copy.error)) : null}
          onSubmit={() => copy.mutate()}
          onClose={() => {
            copy.reset();
            setOpen(false);
          }}
        >
          <ComboBox
            id="copy-shifts-source"
            titleText={t('shiftSetup.copy.from')}
            items={options}
            itemToString={label}
            selectedItem={options.find((o) => o.value === source) ?? null}
            onChange={({ selectedItem }) => setSource(selectedItem?.value)}
          />
          <ComboBox
            id="copy-shifts-target"
            titleText={t('shiftSetup.copy.to')}
            helperText={t('shiftSetup.copy.replaces')}
            items={options.filter((o) => o.value !== source)}
            itemToString={label}
            selectedItem={options.find((o) => o.value === target) ?? null}
            onChange={({ selectedItem }) => setTarget(selectedItem?.value)}
          />
          <Checkbox
            id="copy-shifts-settings"
            labelText={t('shiftSetup.copy.settings')}
            checked={withSettings}
            onChange={(_event, { checked }) => setWithSettings(checked)}
          />
        </FormModal>
      )}
    </>
  );
}
